import './version-button.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'src/components/store';
import CustomButton from '../../shared/custom-button/custom-button';
import { versionStatusAndStateMap } from '../../../model/version';
import { DisableReasons, PolicyType, SavingStatus, VersionLabel } from '../../../utils/enums';
import { SavingStatusIndication } from './saving-status-indication/saving-status-indication';
import { TooltipDisableReason } from '../../shared/tooltip-disable-message/tooltip-disable-reason';
import { IsDraftVersionTooltipShowedKey, VIEW_ONLY_DISABLED_TOOLTIP_TEXT } from '../../../utils/constants';
import { PopoverGotIt } from '../../shared/popover/popover-got-it';
import { VersionButtonHovering } from './version-button-hovering';
import { FwVersion, FwVersionInfo } from 'src/dtos/version.dto';
import ScheduleIcon from '@svg/calendar-clock.svg?react';
import { Tooltip } from 'antd';
import moment from 'moment';

interface VersionButtonProps {
    isShowPartialLabels?: boolean;
    onApplyPolicy: () => void;
    onRestorePolicy: () => void;
    version: FwVersionInfo | FwVersion | undefined;
    scheduleDate: Date | undefined;
}

export const VersionButton: React.FC<VersionButtonProps> = observer(({ version,
                                                                       isShowPartialLabels,
                                                                       onApplyPolicy,
                                                                       onRestorePolicy,
                                                                       scheduleDate }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [hideActive, setHideActive] = useState(true);
    const [showCreateDraftModal, setShowCreateDraftModal] = useState(false);
    const { fwVersionStore, userStore, customerStore } = useStores();
    const previousVersion = useRef<{ externalId: string | undefined, label: VersionLabel | undefined }>({ externalId: undefined, label: undefined });

    const label = useMemo(() =>
        version ? versionStatusAndStateMap.get(version.versionState, version.versionStatus)?.label : undefined
        , [version?.versionState, version?.versionStatus]);

    // Update previousVersion.current with the previous values of label and versionId
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            previousVersion.current = { externalId: version?.externalId, label };
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [label, version]);

    // show modal first time when user creates a draft version
    useEffect(() => {
        const isDirtyCreatedDraftModal = fwVersionStore.isDirty && !localStorage.getItem(IsDraftVersionTooltipShowedKey);
        if (isDirtyCreatedDraftModal) {
            setShowCreateDraftModal(true);
            localStorage.setItem(IsDraftVersionTooltipShowedKey, 'true');
        }
    }, [fwVersionStore.isDirty]);

    const versionDescription = useMemo(() => {
        if (fwVersionStore.savingStatus && fwVersionStore.savingStatus !== SavingStatus.Error) return '';
        if (label === VersionLabel.Pending) return label;
        if (label === VersionLabel.Draft) return 'Apply draft';
        if (label === VersionLabel.Active) {
            if (hideActive && previousVersion.current?.externalId === version?.externalId && previousVersion.current.label === VersionLabel.Pending) {
                setHideActive(false);
                setTimeout(() => setHideActive(true), 5000); // show active label 5 seconds when it replace from pending to active
            }
            return 'Applied';
        }
        if (label && version?.versionNumber) return `${label} V${version.versionNumber}`;
        return '';
    }, [label, version, fwVersionStore.savingStatus, fwVersionStore.latestVersion]);

    const isReadOnlyUserOnDraftVersion = useMemo(() => {
        if (!fwVersionStore.fwVersions) return false;
        const isDraftVersion = label === VersionLabel.Draft;
        return isDraftVersion && userStore.isReadOnlyUser;
    }, [fwVersionStore.draftVersion, label, userStore.isReadOnlyUser]);

    const isShowLabel = () => {
        if (label === VersionLabel.Active) {
            return !hideActive;
        }
        if (isShowPartialLabels) {
            return !!label && [VersionLabel.Draft, VersionLabel.Pending].includes(label);
        }
        return true;
    };

    const hasHoverComponent = useMemo(() => {
        const { savingStatus, pendingVersion } = fwVersionStore;
        if ((!isHovered && label !== VersionLabel.Draft) && !isMenuOpen) return false;
        if (userStore.isReadOnlyUser) return false;
        if (version?.versionType === PolicyType.DEACTIVATE) return false;
        if (label === VersionLabel.Inactive && customerStore.isSourceScheduledVersion) return false;
        if (customerStore.isScheduledVersionUpcoming) return false;
        if ([SavingStatus.Applying, SavingStatus.Applied, SavingStatus.Saving].includes(savingStatus as SavingStatus)) return false;
        // Lockout: hide Apply-draft hover while smart protection draft creation is in progress
        if (customerStore.isSmartDraftCreationInProgress) return false;
        return label === VersionLabel.Draft || (label === VersionLabel.Inactive && !pendingVersion);
    }, [isHovered, isMenuOpen, fwVersionStore.savingStatus, label, fwVersionStore.draftVersion,
        fwVersionStore.pendingVersion, customerStore.isSourceScheduledVersion, customerStore.isScheduledVersionUpcoming,
        customerStore.isSmartDraftCreationInProgress]);

    const loseFocus = () => {
        setIsHovered(false);
        setIsMenuOpen(false);
    };

    if (!version || !isShowLabel())
        return <></>;

    function getDisableReason() {
        if (isReadOnlyUserOnDraftVersion) return VIEW_ONLY_DISABLED_TOOLTIP_TEXT;
        if (customerStore.isSmartDraftCreationInProgress) return DisableReasons.SMART_DRAFT_IN_PROGRESS;
        return '';
    }

    return <PopoverGotIt open={showCreateDraftModal} placement="bottom" overlayClassName="draft-version-popover-explain"
                         onClickGotIt={() => setShowCreateDraftModal(false)}
        text={'You have opened a new draft policy.\nAll policy configuration changes will be auto-saved to this version.\n' +
            'Click the Apply draft button to apply this policy.'}>
        <span className={'version-button-wrapper'}>
            {customerStore.isSourceScheduledVersion &&
                <Tooltip placement={'bottom'}
                         align={{
                             offset: [2, -4] // Adjust the y-offset to move the tooltip up
                         }}
                         title={`Apply policy scheduled for ${moment(scheduleDate).format('MMM DD, yyyy hh:mm A')}`}
                         overlayClassName={'scheduled-info-tooltip'}>
                    <ScheduleIcon/>
                </Tooltip>}
        <span className={`version-button ${label?.toLowerCase()} ${fwVersionStore.savingStatus?.toLowerCase() ?? ''}`}
              onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <TooltipDisableReason
                disableReason={getDisableReason()}>
                {hasHoverComponent ?
                    <VersionButtonHovering label={label}
                                           isShowPartialLabels={isShowPartialLabels}
                                           setIsMenuOpen={setIsMenuOpen}
                                           onApplyPolicy={onApplyPolicy}
                                           onRestorePolicy={onRestorePolicy}
                                           onMouseLeave={loseFocus}
                    />
                    :
                    <CustomButton className={`version-indication-btn primary-btn`}
                                  isDisabled={isReadOnlyUserOnDraftVersion}
                                  label={versionDescription}
                                  prefixIcon={<SavingStatusIndication versionDescription={versionDescription}/>}/>
                }
            </TooltipDisableReason>
        </span>
    </span>
    </PopoverGotIt>;
});